import { registerPlugin } from '@capacitor/core';
import { isNative } from './nativeAuth';

/**
 * Inloggen met Apple, voor beide platformen.
 *
 * Native laat iOS de systeemdialoog tonen en stuurt de authorization code naar de backend.
 * Het web stuurt de browser naar `/auth/apple/start`, dat doorverwijst naar Apple; die post
 * het resultaat terug naar de backend, die de sessiecookies zet en de gebruiker weer
 * hierheen stuurt. Geen Apple JS-SDK en geen popup, dus ook geen popup-blokkades of
 * CSP-gedoe.
 *
 * De code wordt nooit in de browser ingewisseld: dat kan alleen de server, want alleen daar
 * staat de sleutel.
 */

export interface AppleAuthorizeResult {
  code: string;
  /** Alleen bij de allereerste autorisatie; daarna laat Apple deze velden weg. */
  givenName?: string;
  familyName?: string;
}

interface SignInWithApplePlugin {
  authorize(): Promise<AppleAuthorizeResult>;
}

const SignInWithApple = registerPlugin<SignInWithApplePlugin>('SignInWithApple');

/** De gebruiker sloot de Apple-dialoog; dat is geen fout om een melding van te maken. */
export class AppleSignInCancelled extends Error {
  constructor() {
    super('Aanmelding geannuleerd.');
    this.name = 'AppleSignInCancelled';
  }
}

function isCancellation(error: unknown): boolean {
  const code = (error as { code?: string })?.code;
  return code === 'cancelled' || code === 'canceled';
}

/**
 * Toont de Apple-dialoog en geeft de code terug. Alleen native; op het web bestaat de
 * plugin niet en gaat de gebruiker via `startWebSignIn`.
 */
export async function authorizeWithApple(): Promise<AppleAuthorizeResult> {
  try {
    return await SignInWithApple.authorize();
  } catch (error) {
    if (isCancellation(error)) {
      throw new AppleSignInCancelled();
    }
    throw error;
  }
}

/**
 * Stuurt de browser naar de backend, die de aanmelding bij Apple begint. Het pad waar de
 * gebruiker daarna weer terechtkomt gaat mee; de backend controleert dat het een pad binnen
 * de eigen site is.
 */
export function startWebSignIn(apiBaseUrl: string, redirectPath: string = '/'): void {
  const url = `${apiBaseUrl}/auth/apple/start?redirect=${encodeURIComponent(redirectPath)}`;
  window.location.assign(url);
}

/** Op het web is Inloggen met Apple altijd beschikbaar; native hangt het aan de plugin. */
export function isAppleSignInAvailable(): boolean {
  return !isNative() || typeof (SignInWithApple as unknown as { authorize?: unknown }).authorize === 'function';
}

/**
 * Wat de backend na de webflow in de URL zet. `null` betekent: de gebruiker komt hier niet
 * vandaan terug.
 */
export type AppleWebResult = 'ok' | 'error' | null;

export function readAppleWebResult(search: string = window.location.search): AppleWebResult {
  const value = new URLSearchParams(search).get('apple');
  return value === 'ok' || value === 'error' ? value : null;
}

/**
 * Haalt `?apple=` weer uit de adresbalk, zodat een herlaadactie niet opnieuw een melding
 * toont. Vervangt de historie-ingang in plaats van er een toe te voegen.
 */
export function clearAppleWebResult(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('apple');
  window.history.replaceState({}, '', url.pathname + url.search + url.hash);
}
